import { useState, useEffect, useCallback } from 'react';
import { saveUserListPreferences, loadUserListPreferences, PageType } from '../utils/preferencesUtils';

interface UseListPreferencesParams {
  isAuthenticated: boolean;
  currentListId: number | null;
  pageType: PageType;
  persistToSessionStorageImmediately?: boolean; // For pages that want immediate sessionStorage updates
}

interface UseListPreferencesReturn {
  // State
  hiddenStatistics: string[];
  
  // Setters
  setHiddenStatistics: React.Dispatch<React.SetStateAction<string[]>>;
  
  // Handlers
  handleHideStatistic: (statisticKey: string) => void;
  handleShowStatistic: (statisticKey: string) => void;
  
  // Save function (to be called from handleSaveChanges)
  savePreferences: (hiddenStats: string[]) => Promise<void>;
}

/**
 * Hook for managing list preferences (hidden statistics) across List* pages.
 * Handles loading from database (authenticated) or sessionStorage (unauthenticated),
 * and provides handlers for hiding/showing statistics.
 */
export const useListPreferences = ({
  isAuthenticated,
  currentListId,
  pageType,
  persistToSessionStorageImmediately = true, // Default to true to match List* pages behavior
}: UseListPreferencesParams): UseListPreferencesReturn => {
  // Helper to get sessionStorage key based on page type
  const getSessionStorageKey = useCallback(() => {
    const keyMap: Record<PageType, string> = {
      'daily': 'daily',
      'performance': 'performance',
      'technicals': 'technicals',
      'fundamentals': 'fundamentals',
      'analysts': 'analystViews',
      'insiders': 'insiders',
      'prosCons': 'prosCons',
    };
    return keyMap[pageType];
  }, [pageType]);

  // Initialize hiddenStatistics synchronously from sessionStorage (like TickerFundamentals does)
  // This ensures the value is available on first render before useEffect runs
  // Load from sessionStorage for BOTH authenticated and unauthenticated users as initial value
  // (authenticated users will get the correct value from database in useEffect, but this prevents empty state on first render)
  const [hiddenStatistics, setHiddenStatistics] = useState<string[]>(() => {
    // Try to load from sessionStorage immediately (synchronous) for both authenticated and unauthenticated
    const savedHiddenStatistics = sessionStorage.getItem('stocktable-hidden-statistics');
    if (savedHiddenStatistics) {
      try {
        const parsedStats = JSON.parse(savedHiddenStatistics);
        // Get key directly (can't call useCallback in initializer, so inline the logic)
        const keyMap: Record<PageType, string> = {
          'daily': 'daily',
          'performance': 'performance',
          'technicals': 'technicals',
          'fundamentals': 'fundamentals',
          'analysts': 'analystViews',
          'insiders': 'insiders',
          'prosCons': 'prosCons',
        };
        const key = keyMap[pageType];
        
        // Handle both old flat array structure and new categorized structure
        if (Array.isArray(parsedStats)) {
          return parsedStats;
        } else if (parsedStats[key]) {
          return parsedStats[key];
        }
      } catch (error) {
        console.error('Error parsing hidden statistics from sessionStorage during initialization:', error);
      }
    }
    // If no sessionStorage data, start with empty array
    // Will be loaded from database (authenticated) or set later (unauthenticated) in useEffect
    return [];
  });

  // Helper to persist to sessionStorage
  const persistToSessionStorage = useCallback((stats: string[]) => {
    const existingHiddenStats = sessionStorage.getItem('stocktable-hidden-statistics');
    const existingObject = existingHiddenStats ? JSON.parse(existingHiddenStats) : {};
    const key = getSessionStorageKey();
    const updatedObject = {
      ...existingObject,
      [key]: stats,
    };
    sessionStorage.setItem('stocktable-hidden-statistics', JSON.stringify(updatedObject));
  }, [getSessionStorageKey]);

  // Load user preferences when list changes or user logs in
  useEffect(() => {
    const loadPreferences = async () => {
      if (isAuthenticated && currentListId) {
        try {
          const savedPreferences = await loadUserListPreferences(currentListId, pageType);
          setHiddenStatistics(savedPreferences); // Always set, even if empty array
          if (savedPreferences.length > 0) {
            console.log(`✅ Loaded ${pageType} preferences:`, savedPreferences);
          } else {
            console.log(`✅ No ${pageType} preferences found, showing all indicators`);
          }
        } catch (error) {
          console.log(`No ${pageType} preferences found or error loading:`, error);
          setHiddenStatistics([]); // Reset to show all indicators when no preferences
        }
      } else {
        // Reset to show all indicators when not authenticated or no list selected
        setHiddenStatistics([]);
      }
    };

    loadPreferences();
  }, [isAuthenticated, currentListId, pageType]);

  // Load hidden statistics from sessionStorage for unauthenticated users
  // Note: This is a fallback for when sessionStorage changes externally
  // The initial value is already loaded synchronously in useState above
  useEffect(() => {
    if (!isAuthenticated) {
      const savedHiddenStatistics = sessionStorage.getItem('stocktable-hidden-statistics');
      if (savedHiddenStatistics) {
        try {
          const parsedStats = JSON.parse(savedHiddenStatistics);
          const key = getSessionStorageKey();
          
          // Handle both old flat array structure and new categorized structure
          let newStats: string[] = [];
          if (Array.isArray(parsedStats)) {
            newStats = parsedStats;
          } else if (parsedStats[key]) {
            newStats = parsedStats[key];
          }
          
          // Only update if different from current state (avoid unnecessary re-renders)
          setHiddenStatistics(prev => {
            if (JSON.stringify(prev.sort()) === JSON.stringify(newStats.sort())) {
              return prev;
            }
            return newStats;
          });
        } catch (error) {
          console.error('Error parsing hidden statistics from sessionStorage:', error);
        }
      }
    }
  }, [isAuthenticated, getSessionStorageKey]);

  // Handle hiding a statistic
  const handleHideStatistic = useCallback((statisticKey: string) => {
    setHiddenStatistics(prev => {
      const newHiddenStats = [...prev, statisticKey];
      
      // Persist to sessionStorage immediately if requested (for unauthenticated users or when explicitly enabled)
      if (persistToSessionStorageImmediately && !isAuthenticated) {
        persistToSessionStorage(newHiddenStats);
      }
      
      return newHiddenStats;
    });
  }, [isAuthenticated, persistToSessionStorageImmediately, persistToSessionStorage]);

  // Handle showing a statistic
  const handleShowStatistic = useCallback((statisticKey: string) => {
    setHiddenStatistics(prev => {
      const newHiddenStats = prev.filter(key => key !== statisticKey);
      
      // Persist to sessionStorage immediately if requested (for unauthenticated users or when explicitly enabled)
      if (persistToSessionStorageImmediately && !isAuthenticated) {
        persistToSessionStorage(newHiddenStats);
      }
      
      return newHiddenStats;
    });
  }, [isAuthenticated, persistToSessionStorageImmediately, persistToSessionStorage]);

  // Save preferences to database (to be called from handleSaveChanges)
  const savePreferences = useCallback(async (hiddenStats: string[]) => {
    if (isAuthenticated && currentListId) {
      try {
        await saveUserListPreferences(currentListId, pageType, hiddenStats);
        console.log(`✅ ${pageType} preferences saved successfully:`, hiddenStats);
      } catch (error) {
        console.error(`❌ Failed to save ${pageType} preferences:`, error);
        throw error;
      }
    } else {
      // Save to sessionStorage for unauthenticated users
      persistToSessionStorage(hiddenStats);
    }
  }, [isAuthenticated, currentListId, pageType, persistToSessionStorage]);

  return {
    hiddenStatistics,
    setHiddenStatistics,
    handleHideStatistic,
    handleShowStatistic,
    savePreferences,
  };
};

